import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Typography, Chip, Button } from '@material-ui/core';
import StoriesLinksPane from './StoriesLinksPane';
import StoryIntegrityBreakdown from './StoryIntegrityBreakdown';
import { SERVER_NAME } from './config';

const useStyles = makeStyles(() => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    backgroundColor: 'rgba(5,8,20,0.98)',
    minHeight: '100vh',
    padding: 14,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflowY: 'auto',
  },
  card: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background:
      'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.22), rgba(10,14,30,0.98))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    padding: 14,
    marginBottom: 12,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.2,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 12,
    color: '#cfd8ff',
    lineHeight: 1.45,
  },
  metaRow: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    color: '#9ea4d8',
    fontSize: 11,
  },
  chip: {
    height: 22,
    fontSize: 11,
    backgroundColor: 'rgba(77, 121, 255, 0.12)',
    border: '1px solid rgba(77, 121, 255, 0.35)',
    color: '#e5e7ff',
  },
  sectionLabel: {
    marginTop: 10,
    marginBottom: 6,
    fontSize: 12,
    fontWeight: 800,
    color: '#e5e7ff',
  },
  bullets: {
    margin: 0,
    paddingLeft: 18,
    color: '#cfd8ff',
    fontSize: 12,
    lineHeight: 1.5,
  },
  error: {
    padding: 16,
    borderRadius: 12,
    border: '1px solid rgba(255, 120, 120, 0.35)',
    backgroundColor: 'rgba(30, 10, 14, 0.75)',
    color: '#ffd7d7',
    fontSize: 12,
  },
  loadingRow: {
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: '#cfd3ff',
  },

  linksPane: {
    height: '70vh',
    minHeight: 520,
    display: 'flex',
    flexDirection: 'column',
  },
}));

function useQuery(location) {
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

function toSafeText(val) {
  if (val == null) return '';
  if (typeof val === 'string') {
    const t = val.trim();
    if (!t) return '';
    if (/^\[object\s+[^\]]+\]$/i.test(t)) return '';
    return t;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  return '';
}

export default function StoryEmbedPage() {
  const classes = useStyles();
  const { storyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery(location);

  const token = query.get('t') || '';

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    setStory(null);

    const run = async () => {
      try {
        const qs = new URLSearchParams();
        if (token) qs.set('t', token);
        const resp = await fetch(`${SERVER_NAME}/embed/story?${qs.toString()}`);
        if (!resp.ok) {
          const message = resp.status === 401 ? 'This embed link is invalid or expired.' : `Failed to load story (HTTP ${resp.status})`;
          if (mounted) setError(message);
          return;
        }
        const data = await resp.json();
        if (mounted) setStory(data || null);
      } catch (e) {
        if (mounted) setError('Failed to load embed story');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [storyId, token]);

  const evidenceBullets = useMemo(() => {
    const meta = story && story.meta ? story.meta : null;
    const takeaways = meta && Array.isArray(meta.ai_takeaways) ? meta.ai_takeaways : [];
    if (takeaways.length) return takeaways.map((t) => String(t));
    return [];
  }, [story]);

  const rabbitMeta = useMemo(() => {
    if (!story) return null;
    return story.meta && typeof story.meta === 'object' ? story.meta : null;
  }, [story]);

  return (
    <div className={classes.root}>
      {loading ? (
        <div className={classes.loadingRow}>
          <CircularProgress size={18} />
          <Typography variant="body2" style={{ color: '#cfd3ff' }}>Loading…</Typography>
        </div>
      ) : error ? (
        <div className={classes.error}>
          <div style={{ marginBottom: 10 }}>{error}</div>
          <Button
            variant="contained"
            style={{ backgroundColor: 'rgba(77,121,255,0.85)', color: '#fff' }}
            onClick={() => navigate('/stories')}
          >
            Open SpacePacket
          </Button>
        </div>
      ) : !story ? (
        <div className={classes.error}>Story not found.</div>
      ) : (
        <>
          <div className={classes.card}>
            <div className={classes.titleRow}>
              <div style={{ minWidth: 0 }}>
                <div className={classes.title}>{story.title || 'Untitled story'}</div>
                <div className={classes.subtitle}>
                  {toSafeText(typeof story.ai_narrative === 'string' ? story.ai_narrative : (typeof story.subtitle === 'string' ? story.subtitle : ''))}
                </div>
              </div>
            </div>

            <div className={classes.metaRow}>
              {story.story_type && <Chip size="small" className={classes.chip} label={story.story_type} />}
              {story.chain && <Chip size="small" className={classes.chip} label={story.chain} />}
              {story.primary_token && (
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {String(story.primary_token)}
                </span>
              )}
            </div>

            {!!evidenceBullets.length && (
              <>
                <div className={classes.sectionLabel}>Evidence Summary</div>
                <ul className={classes.bullets}>
                  {evidenceBullets.slice(0, 6).map((b, idx) => (
                    <li key={idx}>{b}</li>
                  ))}
                </ul>
              </>
            )}

            <StoryIntegrityBreakdown story={story} />
          </div>

          <div className={classes.linksPane}>
            <StoriesLinksPane
              token={story.primary_token}
              primaryTokenMeta={null}
              showGraph={true}
              isMobile={true}
              sideBySide={false}
              forceShowList={true}
              graphFlex={1}
              listFlex={1}
              onPromotePrimary={() => {}}
              onUpdatingChange={() => {}}
              rabbitMeta={rabbitMeta}
              onSnapshot={() => {}}
            />
          </div>
        </>
      )}
    </div>
  );
}
